import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, MenuItem, TextField } from "@mui/material";
import { managementService } from "../../../../api/services/management";
import Chart from "react-apexcharts";
import { endpoints } from "../../../../api/endpoints";
import { recordsService, rowsOf } from "../../../../api/services/records";
import useRemote from "../../../../hooks/useRemote";
import { MigrationPage, RemoteTable } from "../../../shares/migration/MigrationWidgets";
import Search from "../../../shares/search/Search";

const yearsColumns = [
  { id: "study_year", label: "سال تحصیلی" }, { id: "grade", label: "پایه" },
  { id: "group_name", label: "گروه" }, { id: "status", label: "وضعیت" },
  { id: "exam_average", label: "میانگین آزمون (درصد)" },
  { id: "assignment_average", label: "میانگین تکلیف (درصد)" },
  { id: "attendance_absent", label: "غیبت" },
];
export function StudentRecords() {
  const [params, setParams] = useSearchParams();
  const resource = useRemote((signal) => recordsService.read(endpoints.records.students, Object.fromEntries(params), signal), params.toString());
  return <MigrationPage title="آرشیو دانش‌آموزان">
    <Search getFiltered={{ reFetch: resource.reload }} refreshOnQueryChange={false} />
    <RemoteTable resource={resource} params={params} setParams={setParams} columns={[
      { id: "id", label: "کد ملی", render: (row) => <Link to={"/admin/student-records/" + encodeURIComponent(String(row.id))}><bdi>{row.id}</bdi></Link> },
      { id: "full_name", label: "نام", render: (row) => row.full_name || row.first_name + " " + row.last_name },
      { id: "year_count", label: "تعداد سال‌ها" },
    ]} />
  </MigrationPage>;
}
export function YearRecords() {
  const [params, setParams] = useSearchParams();
  const resource = useRemote((signal) => recordsService.read(endpoints.records.years, Object.fromEntries(params), signal), params.toString());
  return <MigrationPage title="گزارش سالانه">
    <form className="flex flex-wrap gap-4" onSubmit={(event) => {
      event.preventDefault(); setParams(new URLSearchParams(new FormData(event.currentTarget)));
    }}>
      {[["study_year", "سال تحصیلی"], ["grade", "پایه"], ["status", "وضعیت"], ["group_name", "گروه"], ["search", "جست‌وجو"]].map(([name, label]) =>
        <TextField key={name} name={name} label={label} defaultValue={params.get(name) ?? ""} />)}
      <button className="rounded-soft bg-primary text-white p-4">اعمال فیلترها</button>
    </form>
    <RemoteTable resource={resource} columns={yearsColumns} params={params} setParams={setParams} />
  </MigrationPage>;
}
export function History() {
  const { nationalId } = useParams();
  const detail = useRemote((signal) => recordsService.read(endpoints.records.student(nationalId), undefined, signal), nationalId);
  const history = useRemote((signal) => recordsService.read(endpoints.records.history(nationalId), undefined, signal), nationalId);
  return <MigrationPage title={"سوابق " + nationalId}>
    <Link to="/admin/student-records">بازگشت به آرشیو</Link>
    <h2>{detail.data?.full_name || [detail.data?.first_name, detail.data?.last_name].filter(Boolean).join(" ")}</h2>
    {detail.error && <Alert severity="error">{detail.error.message}</Alert>}
    {history.data && <section className="rounded-card bg-surface p-xl space-y-4">
      <h2>وضعیت امسال</h2>
      {history.data.current === null ? <p>حساب فعال برای امسال وجود ندارد.</p> : <dl className="grid grid-cols-2 gap-4">
        {[["is_active", "فعال"], ["group", "گروه"], ["student_status", "وضعیت"], ["grade", "پایه"], ["attendance_total", "جلسات"], ["attendance_present", "حضور"], ["attendance_absent", "غیبت"]].map(([key, label]) =>
          <React.Fragment key={key}><dt>{label}</dt><dd>{typeof history.data.current?.[key] === "boolean" ? (history.data.current[key] ? "بله" : "خیر") : history.data.current?.[key]?.name ?? history.data.current?.[key] ?? "—"}</dd></React.Fragment>)}
      </dl>}
    </section>}
    <h2>سوابق سال‌ها</h2>
    <RemoteTable resource={{ ...history, data: history.data?.year_records }} columns={yearsColumns} />
    <YearDetails years={history.data?.year_records} />
  </MigrationPage>;
}
function YearDetails({ years }) {
  return rowsOf(years).map((year, index) => <details className="rounded-card bg-surface p-4" key={year.id ?? index}>
    <summary>ریز سوابق {year.study_year}</summary>
    <p className="whitespace-pre-wrap">خلاصه مالی: {year.financial_summary || "—"}</p>
    {[["exam_records", "آزمون‌ها"], ["assignment_records", "تکالیف"]].map(([key, label]) => <div key={key}>
      <h3>{label}</h3><RemoteTable resource={{ data: year[key], loading: false }} columns={[
        { id: "title", label: "عنوان" }, { id: "score", label: "نمره (درصد)" }, { id: "date", label: "تاریخ" },
      ]} /></div>)}
  </details>);
}
export function StudentHistory({ id }) {
  const years = useRemote((signal) => recordsService.read(endpoints.records.userYears(id), undefined, signal), String(id), Boolean(id));
  const attendance = useRemote((signal) => recordsService.read(endpoints.records.attendance(id), undefined, signal), String(id), Boolean(id));
  const rows = rowsOf(years.data);
  return <section className="space-y-6">
    <h2 className="text-xl">عملکرد سالانه</h2>
    {years.error && <Alert severity="error">{years.error.message}</Alert>}
    {rows.length > 0 && <div className="grid md:grid-cols-2 gap-6">
      {[["exam_average", "میانگین آزمون (درصد)"], ["assignment_average", "میانگین تکلیف (درصد)"]].map(([key, label]) =>
        <Chart key={key} type="bar" height={240} options={{ chart: { toolbar: { show: false } },
          xaxis: { categories: rows.map((row) => row.study_year) }, title: { text: label } }}
          series={[{ name: label, data: rows.map((row) => row[key] == null ? null : Number(row[key])) }]} />)}
    </div>}
    <YearDetails years={rows} />
    <h2>حضور و غیاب حضوری</h2>
    <p>جلسات: {attendance.data?.total_sessions ?? "—"} · حاضر: {attendance.data?.present_count ?? "—"} · غایب: {attendance.data?.absent_count ?? "—"}</p>
    <RemoteTable resource={{ ...attendance, data: attendance.data?.records }} columns={[
      { id: "session_title", label: "جلسه" }, { id: "group_name", label: "گروه" }, { id: "date", label: "تاریخ" },
      { id: "present", label: "حضور", render: (row) => row.present ? "حاضر" : "غایب" },
    ]} />
  </section>;
}
export function OfflineExams() {
  const [params, setParams] = useSearchParams();
  const resource = useRemote((signal) => recordsService.offline(Object.fromEntries(params), signal), params.toString());
  const groups = useRemote((signal) => managementService.allGroups(signal));
  const filterKey = new URLSearchParams(params); filterKey.delete("page");
  return <MigrationPage title="نتایج آزمون‌های حضوری">
    {groups.error && <Alert severity="error">{groups.error.message}</Alert>}
    {params.get("exam") && <Button onClick={() => setParams((previous) => {
      const next = new URLSearchParams(previous); next.delete("exam"); next.delete("page"); return next;
    })}>نمایش همه آزمون‌ها</Button>}
    <form key={filterKey.toString()} className="flex flex-wrap gap-4" onSubmit={(event) => {
      event.preventDefault();
      const next = new URLSearchParams(params); next.delete("page");
      for (const [key, value] of new FormData(event.currentTarget)) {
        if (String(value).trim()) next.set(key, String(value).trim()); else next.delete(key);
      }
      setParams(next);
    }}>
      <TextField name="search" label="نام، کد ملی یا نام آزمون" defaultValue={params.get("search") || ""} />
      <TextField select name="exam__exam_group" label="گروه" defaultValue={params.get("exam__exam_group") || ""}
        disabled={groups.loading || Boolean(groups.error)} className="min-w-[16rem]">
        <MenuItem value="">همه گروه‌ها</MenuItem>
        {rowsOf(groups.data).map((group) => <MenuItem key={group.id} value={String(group.id)}>{group.name}</MenuItem>)}
      </TextField>
      <TextField select name="exam_peresence" label="حضور" defaultValue={params.get("exam_peresence") || ""}>
        <MenuItem value="">همه</MenuItem><MenuItem value="true">حاضر</MenuItem><MenuItem value="false">غایب</MenuItem>
      </TextField>
      <TextField select name="countable" label="محاسبه در میانگین" defaultValue={params.get("countable") || ""}>
        <MenuItem value="">همه</MenuItem><MenuItem value="true">بله</MenuItem><MenuItem value="false">خیر</MenuItem>
      </TextField>
      <TextField select name="ordering" label="مرتب‌سازی" defaultValue={params.get("ordering") || ""}>
        <MenuItem value="">پیش‌فرض</MenuItem><MenuItem value="-score">بیشترین نمره</MenuItem>
        <MenuItem value="score">کمترین نمره</MenuItem><MenuItem value="-updated_at">جدیدترین تغییر</MenuItem>
        <MenuItem value="updated_at">قدیمی‌ترین تغییر</MenuItem>
      </TextField>
      <Button type="submit" variant="contained">اعمال فیلترها</Button>
    </form>
    <RemoteTable resource={resource} params={params} setParams={setParams} columns={[
      { id: "exam_name", label: "آزمون" }, { id: "group_name", label: "گروه" },
      { id: "exam_date", label: "تاریخ", render: (row) => {
        const date = new Date(row.exam_date);
        return row.exam_date && Number.isFinite(date.getTime()) ? date.toLocaleDateString("fa-IR") : "—";
      } },
      { id: "username", label: "کد ملی", render: (row) => <bdi>{row.username}</bdi> },
      { id: "student_name", label: "دانش‌آموز", render: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") },
      { id: "score", label: "درصد" },
      { id: "exam_peresence", label: "حضور", render: (row) => row.exam_peresence ? "حاضر" : "غایب" },
    ]} />
  </MigrationPage>;
}
